import { useEffect, useState } from "react";
import { useI18n } from "@/context/I18nContext";
import client, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Megaphone, Send, Loader2, Users, Building2, UserCog, ChevronDown, ChevronUp, Info, AlertTriangle, BellRing } from "lucide-react";
import { toast } from "sonner";

const SEV = [
  { key: "info", label: "Info", icon: Info, cls: "bg-slate-100 text-slate-700 border-slate-200", on: "bg-slate-700 text-white border-slate-700" },
  { key: "warning", label: "Warning", icon: BellRing, cls: "bg-amber-50 text-amber-700 border-amber-200", on: "bg-amber-500 text-white border-amber-500" },
  { key: "urgent", label: "Urgent", icon: AlertTriangle, cls: "bg-red-50 text-red-700 border-red-200", on: "bg-red-600 text-white border-red-600" },
];
const SEV_BADGE = { info: "bg-slate-100 text-slate-700", warning: "bg-amber-100 text-amber-800", urgent: "bg-red-100 text-red-800" };

function Chips({ items, value, onToggle, testid, getId, getLabel }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {items.length === 0 && <span className="text-xs text-slate-400">—</span>}
      {items.map((it) => {
        const id = getId(it);
        const on = value.includes(id);
        return (
          <button type="button" key={id} data-testid={`${testid}-${id}`} onClick={() => onToggle(id)}
            className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition ${on ? "bg-brand text-white border-brand" : "bg-white border-slate-200 text-slate-600 hover:border-brand/40"}`}>
            {getLabel(it)}
          </button>
        );
      })}
    </div>
  );
}

export default function Alerts() {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [sent, setSent] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [recips, setRecips] = useState({});

  const [severity, setSeverity] = useState("info");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [userIds, setUserIds] = useState([]);
  const [supIds, setSupIds] = useState([]);
  const [siteIds, setSiteIds] = useState([]);
  const [expiresAt, setExpiresAt] = useState("");
  const [sending, setSending] = useState(false);

  const loadSent = () => client.get("/alerts/sent").then((r) => setSent(r.data)).catch(() => {});
  useEffect(() => {
    client.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    client.get("/sites").then((r) => setSites(r.data)).catch(() => {});
    loadSent();
  }, []);

  const supervisors = users.filter((u) => u.role === "supervisor");
  const toggle = (setter) => (id) => setter((v) => (v.includes(id) ? v.filter((x) => x !== id) : [...v, id]));

  const send = async () => {
    if (!title.trim()) return toast.error(t("alerts.needTitle", "Add a title"));
    const targets = [
      ...userIds.map((id) => ({ type: "user", id })),
      ...supIds.map((id) => ({ type: "supervisor", id })),
      ...siteIds.map((id) => ({ type: "site", id })),
    ];
    if (!targets.length) return toast.error(t("alerts.needTarget", "Select at least one recipient"));
    setSending(true);
    try {
      const { data } = await client.post("/alerts", { title, body, severity, targets, expiresAt: expiresAt || null });
      toast.success(t("alerts.sent", "Alert sent to {n} people").replace("{n}", data.recipientCount));
      setTitle(""); setBody(""); setUserIds([]); setSupIds([]); setSiteIds([]); setExpiresAt(""); setSeverity("info");
      loadSent();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const openRecipients = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!recips[id]) {
      try { const { data } = await client.get(`/alerts/${id}/recipients`); setRecips((m) => ({ ...m, [id]: data })); }
      catch { /* noop */ }
    }
  };

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div className="flex items-center gap-2">
        <Megaphone className="h-6 w-6 text-brand" />
        <h2 className="font-display text-2xl font-bold text-slate-900">{t("alerts.title", "Alerts & Notifications")}</h2>
      </div>

      <Card className="p-5 border-slate-200 max-w-3xl" data-testid="alert-compose-card">
        <h3 className="font-semibold text-slate-900 mb-4">{t("alerts.compose", "Send an alert")}</h3>

        <Label className="text-xs uppercase tracking-wide text-slate-500">{t("alerts.severity", "Severity")}</Label>
        <div className="flex gap-2 mt-1.5 mb-4">
          {SEV.map((s) => {
            const on = severity === s.key; const Icon = s.icon;
            return (
              <button key={s.key} type="button" data-testid={`alert-sev-${s.key}`} onClick={() => setSeverity(s.key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold border inline-flex items-center gap-1.5 transition ${on ? s.on : s.cls}`}>
                <Icon className="h-4 w-4" />{s.label}
              </button>
            );
          })}
        </div>

        <div className="space-y-1.5 mb-4">
          <Label>{t("alerts.titleField", "Title")}</Label>
          <Input data-testid="alert-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("alerts.titlePh", "e.g. Site closed tomorrow")} />
        </div>
        <div className="space-y-1.5 mb-4">
          <Label>{t("alerts.messageField", "Message")}</Label>
          <Textarea data-testid="alert-body-input" value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder={t("alerts.messagePh", "Details for your team…")} />
        </div>

        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 mb-4">
          <div className="p-3">
            <p className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 mb-2"><Users className="h-3.5 w-3.5" />{t("alerts.people", "People")}</p>
            <Chips items={users} value={userIds} onToggle={toggle(setUserIds)} testid="alert-users" getId={(u) => u.id} getLabel={(u) => u.displayName} />
          </div>
          <div className="p-3">
            <p className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 mb-2"><UserCog className="h-3.5 w-3.5" />{t("alerts.supervisors", "Supervisor + their team")}</p>
            <Chips items={supervisors} value={supIds} onToggle={toggle(setSupIds)} testid="alert-supervisors" getId={(u) => u.id} getLabel={(u) => u.displayName} />
          </div>
          <div className="p-3">
            <p className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 mb-2"><Building2 className="h-3.5 w-3.5" />{t("alerts.sites", "Everyone at a site")}</p>
            <Chips items={sites} value={siteIds} onToggle={toggle(setSiteIds)} testid="alert-sites" getId={(s) => s.id} getLabel={(s) => s.name} />
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1.5">
            <Label>{t("alerts.expiry", "Auto-expire (optional)")}</Label>
            <Input data-testid="alert-expiry-input" type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="w-56" />
          </div>
          <Button data-testid="alert-send-button" onClick={send} disabled={sending} className="ml-auto">
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}{t("alerts.send", "Send alert")}
          </Button>
        </div>
      </Card>

      <Card className="p-5 border-slate-200 max-w-3xl" data-testid="alert-sent-card">
        <h3 className="font-semibold text-slate-900 mb-4">{t("alerts.sentTitle", "Sent alerts")}</h3>
        {sent.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="alerts-sent-empty">{t("alerts.noneSent", "No alerts sent yet.")}</p>
        ) : (
          <div className="space-y-2">
            {sent.map((a) => (
              <div key={a.id} className="rounded-lg border border-slate-200" data-testid={`alert-sent-${a.id}`}>
                <div className="flex items-center gap-3 p-3">
                  <Badge className={`${SEV_BADGE[a.severity] || SEV_BADGE.info} border-0 capitalize`}>{a.severity}</Badge>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-800 truncate">{a.title}</p>
                    <p className="text-[11px] text-slate-400">{(a.createdAt || "").slice(0, 16).replace("T", " ")}{a.expiresAt ? ` · expires ${a.expiresAt.slice(0, 16).replace("T", " ")}` : ""}</p>
                  </div>
                  <button data-testid={`alert-seen-${a.id}`} onClick={() => openRecipients(a.id)} className="text-xs font-semibold text-brand hover:underline flex items-center gap-1 shrink-0">
                    {t("alerts.seen", "Seen")} {a.seenCount}/{a.recipientCount}
                    {expanded === a.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>
                </div>
                {expanded === a.id && (
                  <div className="border-t border-slate-100 p-3 bg-slate-50/60" data-testid={`alert-recipients-${a.id}`}>
                    {a.body && <p className="text-sm text-slate-600 mb-2">{a.body}</p>}
                    {!recips[a.id] ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : (
                      <div className="flex flex-wrap gap-1.5">
                        {recips[a.id].map((r) => (
                          <span key={r.userId} className={`px-2 py-0.5 rounded-full text-[11px] border ${r.read ? "bg-teal-50 text-teal-700 border-teal-200" : "bg-white text-slate-500 border-slate-200"}`}>
                            {r.name}{r.read ? " ✓" : ""}{r.dismissed ? " · dismissed" : ""}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
